/**
 * Reports Page
 * Phase 7: Generate, track, and download audit reports.
 * 
 * Supports HTML, PDF, and JSON report formats with async generation tracking.
 */

import { useState, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import {
  api,
  ReportFormat,
  ReportStatusResponse,
  AuditDetailsResponse,
  AuditRunListItem,
} from '../lib/api'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { ReportStatusCard } from '../components/ReportStatusCard'
import { EmptyState } from '../components/design-system/EmptyState'
import { StatusBadge } from '../components/design-system/Badge'
import { useLocale } from '../contexts/LocaleContext'

export function ReportsPage() {
  const { t, locale } = useLocale()
  const { auditId } = useParams<{ auditId: string }>()
  const [searchParams] = useSearchParams()
  const auditRunId = auditId || searchParams.get('audit_run_id')
  
  const navigate = useNavigate()
  
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [audit, setAudit] = useState<AuditDetailsResponse | null>(null)
  const [reports, setReports] = useState<ReportStatusResponse[]>([])
  const [recentReports, setRecentReports] = useState<ReportStatusResponse[]>([])
  const [runs, setRuns] = useState<AuditRunListItem[]>([])
  const [generating, setGenerating] = useState(false)
  
  useEffect(() => {
    if (auditRunId) {
      loadReports()
      return
    }
    let cancelled = false
    async function loadIndex() {
      try {
        setLoading(true)
        setError(null)
        const [reportData, auditData] = await Promise.all([
          api.listRecentReports(50),
          api.listAuditRuns({ page: 1, page_size: 50 }),
        ])
        if (cancelled) return
        setRecentReports(reportData)
        setRuns(auditData.audits)
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : t('reports.failedLoad'))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    loadIndex()
    return () => {
      cancelled = true
    }
  }, [auditRunId])
  
  async function loadReports(silent = false) {
    if (!auditRunId) return
    
    try {
      if (!silent) {
        setLoading(true)
        setError(null)
      }
      
      const [auditData, reportsData] = await Promise.all([
        api.getAuditDetails(auditRunId),
        api.listReportsForAudit(auditRunId),
      ])
      
      setAudit(auditData)
      setReports(reportsData)
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message)
      } else {
        setError(t('reports.failedLoad'))
      }
    } finally {
      if (!silent) setLoading(false)
    }
  }
  
  async function handleGenerateReport(format: ReportFormat) {
    if (!auditRunId) return
    
    try {
      setGenerating(true)
      setError(null)
      
      const created = await api.generateReport(auditRunId, { format, locale })
      if (created?.report_id) {
        setReports((prev) => {
          if (prev.some((item) => item.report_id === created.report_id)) return prev
          return [
            {
              report_id: created.report_id,
              audit_run_id: created.audit_run_id,
              format: created.format,
              status: created.status,
              created_at: created.created_at,
              generated_at: null,
              file_size_bytes: null,
              error_message: null,
            },
            ...prev,
          ]
        })
      }
      await loadReports(true)
    } catch (err) {
      if (err instanceof Error) {
        setError(t('reports.generateFail', { msg: err.message }))
      } else {
        setError(t('reports.failedGenerate'))
      }
    } finally {
      setGenerating(false)
    }
  }
  
  async function handleDownload(reportId: string) {
    try {
      const blob = await api.downloadReport(reportId)
      
      // Find the report to get its format
      const report =
        reports.find((r) => r.report_id === reportId) ||
        recentReports.find((r) => r.report_id === reportId)
      if (!report) {
        throw new Error(t('reports.notFound'))
      }
      
      // Create download link
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `audit_report_${auditRunId?.substring(0, 8)}.${report.format}`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      if (err instanceof Error) {
        setError(t('reports.downloadFail', { msg: err.message }))
      } else {
        setError(t('reports.downloadFail', { msg: '' }))
      }
    }
  }
  
  async function handleRefresh(reportId: string) {
    try {
      const status = await api.getReportStatus(reportId)
      
      // Update the report in the list
      setReports((prev) =>
        prev.map((r) => (r.report_id === reportId ? status : r))
      )
      setRecentReports((prev) =>
        prev.map((r) => (r.report_id === reportId ? status : r))
      )
    } catch (err) {
      console.error('Failed to refresh report status:', err)
    }
  }

  useEffect(() => {
    const busy = reports.some((item) => item.status === 'pending' || item.status === 'generating')
    if (!busy || !auditRunId) return
    const timer = window.setInterval(() => {
      void loadReports(true)
    }, 2500)
    return () => window.clearInterval(timer)
  }, [reports, auditRunId])
   
  if (loading) {
    return (
      <div className="space-y-6" data-testid="reports-page">
        <h1 className="text-2xl font-medium mb-1">{t('reports.title')}</h1>
        <LoadingSpinner message={t('reports.loading')} />
      </div>
    )
  }

  if (!auditRunId) {
    const eligible = runs.filter((run) =>
      ['completed', 'partial', 'failed'].includes(run.status)
    )
    return (
      <div className="space-y-6" data-testid="reports-page">
        <header>
          <h1 className="text-2xl font-medium mb-1">{t('reports.title')}</h1>
          <p className="text-sm text-[var(--text-secondary)]">
            {t('reports.indexLead')}
          </p>
        </header>
        {error && <div className="card card-error"><p className="text-sm">{error}</p></div>}
        {recentReports.length > 0 && (
          <section>
            <h2 className="text-lg font-medium mb-3">{t('reports.recent')}</h2>
            <div className="grid gap-4 md:grid-cols-2">
              {recentReports.map((report) => (
                <ReportStatusCard
                  key={report.report_id}
                  report={report}
                  onDownload={handleDownload}
                  onRefresh={handleRefresh}
                />
              ))}
            </div>
          </section>
        )}
        {eligible.length === 0 && recentReports.length === 0 ? (
          <EmptyState
            title={t('reports.noneIndex')}
            description={t('reports.noneIndexBody')}
            action={{ label: t('reports.goAudits'), onClick: () => navigate('/audits') }}
          />
        ) : (
          <section>
            <h2 className="text-lg font-medium mb-3">{t('reports.fromAudit')}</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {eligible.map((run) => (
                <button
                  key={run.id}
                  onClick={() => navigate(`/reports?audit_run_id=${run.id}`)}
                  className="card"
                  style={{ textAlign: 'left', cursor: 'pointer' }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
                    <strong>{run.project_name}</strong>
                    <StatusBadge status={run.status} />
                  </div>
                  <div className="text-sm text-[var(--text-secondary)]">
                    {t('reports.findingsDot', { n: run.total_findings, when: new Date(run.created_at).toLocaleString() })}
                  </div>
                </button>
              ))}
            </div>
          </section>
        )}
      </div>
    )
  }
  
  if (error && !audit) {
    return (
      <div className="card card-error">
        <p className="text-sm mb-4">{error}</p>
        <button
          onClick={() => navigate('/dashboard')}
          className="btn btn-sm btn-primary"
        >
          {t('reports.backDash')}
        </button>
      </div>
    )
  }
  
  if (!audit) {
    return null
  }
  
  const canGenerateReports = 
    audit.status === 'completed' || 
    audit.status === 'partial' || 
    audit.status === 'failed'
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(`/audit/${auditRunId}`)}
          className="btn btn-sm btn-secondary"
        >
          {t('reports.backAudit')}
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-medium mb-1">{t('reports.auditTitle')}</h1>
          <p className="text-sm text-[var(--text-secondary)]">
            {audit.project_name} • {new Date(audit.created_at).toLocaleString(locale === 'ru' ? 'ru-RU' : 'en-US')}
          </p>
        </div>
      </div>
      
      {/* Error message */}
      {error && (
        <div className="card card-error">
          <p className="text-sm">{error}</p>
        </div>
      )}
      
      {/* Generate Report Section */}
      {canGenerateReports && (
        <div className="card">
          <h2 className="text-lg font-medium text-[var(--text-primary)] mb-4">
            {t('reports.generateNew')}
          </h2>
          
          <p className="text-sm text-[var(--text-secondary)] mb-4">
            {t('reports.generateLead')}
          </p>
          
          <div className="flex flex-wrap gap-3">
            <button
              className="btn btn-primary"
              onClick={() => handleGenerateReport('html')}
              disabled={generating}
            >
              {generating ? t('reports.generating') : t('reports.generateHtml')}
            </button>
            
            <button
              className="btn btn-secondary"
              onClick={() => handleGenerateReport('pdf')}
              disabled={generating}
            >
              {generating ? t('reports.generating') : t('reports.generatePdf')}
            </button>
            
            <button
              className="btn btn-secondary"
              onClick={() => handleGenerateReport('json')}
              disabled={generating}
            >
              {generating ? t('reports.generating') : t('reports.generateJson')}
            </button>
          </div>
          <p className="text-xs text-[var(--text-tertiary)] mt-3">
            {t('reports.htmlHint')}
          </p>
          {(generating || reports.some((item) => item.status === 'pending' || item.status === 'generating')) && (
            <div className="mt-4">
              <p className="text-sm text-[var(--text-secondary)] mb-2">{t('reports.building')}</p>
              <div className="audit-progress-track report-progress-bar" role="progressbar" aria-label={t('reports.building')}>
                <div className="audit-progress-seg audit-progress-seg-run" style={{ width: '40%' }} />
                <div className="audit-progress-seg audit-progress-seg-wait" style={{ width: '60%' }} />
              </div>
            </div>
          )}
        </div>
      )}
      
      {!canGenerateReports && (
        <div className="card bg-[var(--surface-secondary)]">
          <div className="flex items-start gap-3">
            <svg
              className="w-5 h-5 text-[var(--text-tertiary)] flex-shrink-0 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div>
              <div className="text-sm font-medium text-[var(--text-primary)] mb-1">
                {t('reports.notAvailable')}
              </div>
              <div className="text-sm text-[var(--text-secondary)]">
                {t('reports.notAvailableBody', { status: audit.status })}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Existing Reports */}
      {reports.length > 0 && (
        <div>
          <h2 className="text-lg font-medium text-[var(--text-primary)] mb-4">
            {t('reports.generated')}
          </h2>
          
          <div className="grid gap-4 md:grid-cols-2">
            {reports.map((report) => (
              <ReportStatusCard
                key={report.report_id}
                report={report}
                onDownload={handleDownload}
                onRefresh={handleRefresh}
              />
            ))}
          </div>
        </div>
      )}
      
      {reports.length === 0 && canGenerateReports && (
        <div className="card text-center py-8">
          <svg
            className="w-12 h-12 mx-auto text-[var(--text-tertiary)] mb-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <p className="text-sm text-[var(--text-secondary)]">
            {t('reports.noneYet')}
          </p>
        </div>
      )}
    </div>
  )
}
